import {
  Badge,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { FC, useContext, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { SidebarMenuItem } from '@components/Layout/Sidebar/constants';
import { darkBlue, lightGrey2, primaryBlue, white } from '@styles/colors';
import { normal, semibold } from '@styles/fontWeights';
import { useLogout } from '@features/applications/hooks/useLogout';
import { useLoginBack } from '@features/superAdmin/hooks/useLoginBack';
import { getExactCookie } from '@utils/getCookie';
import { authContext } from '@staffedupusa/shared-components';
import { permissionsList } from '@features/profile/components/InviteUsersModal/constants';
import { hasPermission } from '@features/profile/components/InviteUsersModal/PermissionUtils';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

type SidebarNavListItemProps = {
  item: SidebarMenuItem;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
};

const SidebarNavListItem: FC<SidebarNavListItemProps> = ({
  item,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  const { authData } = useContext(authContext);
  const isAdmin = authData?.user?.is_admin;
  const [loading, setLoading] = useState<Boolean>(false);
  const { link, Icon, text } = item;
  const location = useLocation();
  const navigate = useNavigate();
  const isCurrentPage =
    location.pathname?.replace(/\/$/, '') === link?.replace(/\/$/, '') &&
    text !== 'Hiring Checklist';
  const { userLoginBack } = useLoginBack();
  const masterKey = getExactCookie('master_key');

  const handleClick = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (text === 'Logout') {
      await useLogout({ setLoading });
    } else if (text === 'Back to Dashboard' && masterKey) {
      userLoginBack({
        user_id: masterKey,
      });
    }
  };

  const getButtonColor = () => {
    if (item?.buttonColor) {
      return {
        bgcolor: item.buttonColor,
        color: white,
      };
    }

    return {
      color: isCurrentPage ? white : isAdmin ? lightGrey2 : darkBlue,
      bgcolor: isCurrentPage ? 'primary.main' : 'transparent',
    };
  };
  return (
    <ListItem disablePadding>
      {text === 'Logout' || text === 'Back to Dashboard' ? (
        //@ts-ignore
        <ListItemButton
          component={Button}
          onClick={handleClick}
          sx={{
            px: 2.5,
            color: getButtonColor().color,
            bgcolor: getButtonColor().bgcolor,
            fontWeight: isCurrentPage ? semibold : normal,
            mt: item?.buttonColor ? 2 : 0,
            borderRadius: 5,
            whiteSpace: 'break-spaces',
          }}
        >
          <Stack spacing={3} direction="row" alignItems="center">
            <Badge badgeContent={item.badgeCount} color="error">
              <ListItemIcon
                sx={{
                  minWidth: 'initial',
                  color: 'inherit',
                }}
              >
                <Icon size="large" sx={{ fontSize: '23px' }} />
              </ListItemIcon>
            </Badge>
            {isSidebarOpen && (
              <Typography
                component="p"
                width="120px"
                fontWeight="inherit"
                style={{ marginLeft: '8px' }}
              >
                {text}{' '}
                {loading && text === 'Logout' && (
                  <CircularProgress size="20px" />
                )}
              </Typography>
            )}
          </Stack>
        </ListItemButton>
      ) : (
        <ListItemButton
          disabled={
            (authData?.user?.company && authData?.user?.company?.plan === 0) ||
            (authData?.user?.company && !authData?.user?.enabled) ||
            (authData?.user?.company &&
              authData?.user?.company?.is_suspicious &&
              authData?.user?.company?.plan !== 0) ||
            link === '/enterprise/account#WOTC' ||
            (link === '/employer/account#Manage-Users' &&
              !authData?.user?.master_account) ||
            (link === '/employer/application-questions/' &&
              !hasPermission(
                permissionsList.applicationQuestions,
                //@ts-ignore
                //have to update user form private package
                authData.user?.permissions?.permissions,
              ))
              ? true
              : false
          }
          sx={{
            px: 2.5,
            color: isCurrentPage
              ? white
              : text === 'Hiring Checklist'
                ? primaryBlue
                : isAdmin
                  ? lightGrey2
                  : darkBlue,
            bgcolor: isCurrentPage
              ? 'primary.main'
              : text === 'Hiring Checklist'
                ? 'white'
                : isAdmin
                  ? 'transparent'
                  : 'white',
            fontWeight:
              isCurrentPage || text === 'Hiring Checklist' ? semibold : normal,
            borderRadius: 5,
            whiteSpace: 'break-spaces',
            border:
              text === 'Hiring Checklist' ? `2px solid ${primaryBlue}` : 'none',
            mb: text === 'Hiring Checklist' ? 2 : 0,
            '&:hover': {
              bgcolor: text === 'Hiring Checklist' ? 'white' : lightGrey2,
              color: text === 'Hiring Checklist' ? primaryBlue : darkBlue,
            },
          }}
          id={`button-${text.toLowerCase().replace(/\s+/g, '-')}`}
          onClick={(e) => {
            e.preventDefault();
            if (text === 'Hiring Checklist') {
              track(amplitudeEventsName.CHECKLIST_NAV_BUTTON);
            }
            if (text === 'Search All Jobs') {
              const { city, state, s_lat, s_lon } = authData?.user || {};

              let finalLink = link;

              if (city && state) {
                const url = new URL(link);
                url.searchParams.set('city_name', `${city}, ${state}`);
                if (s_lat) url.searchParams.set('lat', String(s_lat));
                if (s_lon) url.searchParams.set('lon', String(s_lon));
                finalLink = url.toString();
              }

              window.open(finalLink, '_blank', 'noopener,noreferrer');
              return;
            } else {
              navigate(link);
            }
          }}
        >
          <Stack spacing={3} direction="row" alignItems="center">
            <Badge badgeContent={item.badgeCount} color="error">
              <ListItemIcon
                sx={{
                  minWidth: 'initial',
                  color: 'inherit',
                }}
              >
                <Icon size="large" sx={{ fontSize: '23px' }} />
              </ListItemIcon>
            </Badge>
            {isSidebarOpen && (
              <Typography
                component="p"
                width="120px"
                fontWeight="inherit"
                style={{ marginLeft: '8px' }}
              >
                {text}
              </Typography>
            )}
          </Stack>
        </ListItemButton>
      )}
    </ListItem>
  );
};

export default SidebarNavListItem;
